/**
 * New meeting (Aziz, 2026-09-27). Team only: pick people and a time; if everyone is
 * free the invites go out at once, otherwise the grid shows who is busy and why. With
 * a clinic: pick our people, the clinic and who to invite there; they get a page of
 * the times all of us are free, and the first pick books it for everyone.
 *
 * Opened from Meetings, from a creative (pre-filled with its script), from a shoot
 * (its clinic, crew and creatives) and from a meeting (a follow-up).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarPlus, Check, Copy, MapPin, Plus, Search, Users, Video, X } from 'lucide-react';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import AvailabilityGrid from '@/components/availability/AvailabilityGrid';
import { fetchMeetingSetup, planMeeting, type Conflict, type Meeting, type PlanBody, type PlanItem } from '@/lib/calls';
import { fetchFreeTimes } from '@/lib/availability';

export interface MeetingPrefill {
  kind?: 'internal' | 'client';
  title?: string;
  clientIds?: string[];
  attendeeIds?: string[];
  items?: Array<PlanItem & { label: string }>;
  inviteeEmails?: string[];
  followUpOf?: string;
  shootId?: string;
}

const LENGTHS = [15, 30, 45, 60, 90];
const errDetail = (e: unknown) => (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;

function dubaiDay(offset: number): string {
  const d = new Date(Date.now() + offset * 86400000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(d);
}
const dayChip = (day: string) => new Date(`${day}T12:00:00+04:00`).toLocaleDateString('en-GB', { timeZone: 'Asia/Dubai', weekday: 'short', day: 'numeric', month: 'short' });
const whenLabel = (iso: string) => new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const REASON: Record<Conflict['reason'], string> = { off: 'is off', meeting: 'has a meeting', shoot: 'is on a shoot', hours: 'is outside working hours' };

export default function NewMeetingDialog({ open, onClose, prefill }: { open: boolean; onClose: () => void; prefill?: MeetingPrefill }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [kind, setKind] = useState<'internal' | 'client'>(prefill?.kind ?? 'internal');
  const [title, setTitle] = useState(prefill?.title ?? '');
  const [attendees, setAttendees] = useState<string[]>(prefill?.attendeeIds?.filter((i) => i !== user?.id) ?? []);
  const [clinicIds, setClinicIds] = useState<string[]>(prefill?.clientIds ?? []);
  const [invitees, setInvitees] = useState<string[]>(prefill?.inviteeEmails ?? []);
  const [extra, setExtra] = useState<Array<{ email: string; name: string }>>([]);
  const [adding, setAdding] = useState({ name: '', email: '' });
  const [items, setItems] = useState<Array<PlanItem & { label: string }>>(prefill?.items ?? []);
  const [topic, setTopic] = useState('');
  const [search, setSearch] = useState('');
  const [peopleFilter, setPeopleFilter] = useState('');
  const [minutes, setMinutes] = useState(30);
  // the organiser may only book it for others: then their own hours do not limit the times
  const [iAttend, setIAttend] = useState(true);
  const [withMeet, setWithMeet] = useState(true);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [day, setDay] = useState(dubaiDay(1));
  const [start, setStart] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<Conflict[] | null>(null);
  const [sent, setSent] = useState<Meeting | null>(null);

  useEffect(() => { if (open) { setSent(null); } }, [open]);

  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(search), 250); return () => clearTimeout(t); }, [search]);
  const setup = useQuery({ queryKey: ['meeting-setup', clinicIds.join(','), debounced], queryFn: () => fetchMeetingSetup(clinicIds, debounced), enabled: open });
  const data = setup.data;
  const people = useMemo(() => [iAttend ? user?.id : null, ...attendees].filter(Boolean) as string[], [user?.id, attendees, iAttend]);

  const free = useQuery({
    queryKey: ['free-times', people.join(','), minutes],
    queryFn: () => fetchFreeTimes(people, minutes, 10),
    enabled: open && people.length > 0,
  });
  const best = (free.data?.slots ?? []).slice(0, 3);

  // re-check the picked time whenever the people, length or time change
  useEffect(() => {
    setConflicts(null);
    if (!start || kind !== 'internal') return;
    let live = true;
    api.post<{ free: boolean; conflicts: Conflict[] }>('/availability/check', { user_ids: people, start, minutes })
      .then((r) => { if (live) setConflicts(r.data.conflicts); }).catch(() => undefined);
    return () => { live = false; };
  }, [start, people.join(','), minutes, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosenInvitees = useMemo(() => {
    const known = (data?.invitees ?? []).filter((i) => invitees.includes(i.email));
    return [...known, ...extra.map((e) => ({ ...e, kind: 'clinic' as const, client_id: clinicIds[0] }))];
  }, [data, invitees, extra, clinicIds]);

  const send = useMutation({
    mutationFn: () => {
      const body: PlanBody = {
        kind, title: title.trim() || undefined, attendee_ids: attendees, client_ids: clinicIds,
        invitees: kind === 'client' ? chosenInvitees.map((i) => ({ email: i.email, name: i.name, kind: i.kind, client_id: i.client_id,
          talent_id: 'talent_id' in i ? i.talent_id : undefined, user_id: 'user_id' in i ? i.user_id : undefined })) : [],
        items: items.map(({ kind: k, id, label }) => (k === 'topic' ? { kind: k, label } : { kind: k, id })),
        minutes, with_meet: withMeet, location: withMeet ? undefined : location.trim() || undefined, notes: notes.trim() || undefined,
        start: kind === 'internal' ? start ?? undefined : undefined, follow_up_of: prefill?.followUpOf, shoot_id: prefill?.shootId,
        organizer_attends: iAttend,
      };
      return planMeeting(body);
    },
    onSuccess: (m) => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
      qc.invalidateQueries({ queryKey: ['free-times'] });
      qc.invalidateQueries({ queryKey: ['availability-grid'] });
      if (m.status === 'booked') { toast.success('Invites sent'); onClose(); nav(`/meetings/${m.id}`); }
      else setSent(m);
    },
    onError: (e) => {
      const d = errDetail(e);
      if (d && typeof d === 'object' && 'conflicts' in (d as object)) {
        setConflicts((d as { conflicts: Conflict[] }).conflicts);
        toast.error('Not everyone is free at that time');
      } else toast.error(typeof d === 'string' ? d : 'Could not create the meeting');
    },
  });

  if (!open) return null;
  const toggle = (arr: string[], set: (v: string[]) => void, v: string) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const team = (data?.team ?? []).filter((p) => p.id !== user?.id && (!peopleFilter || p.name.toLowerCase().includes(peopleFilter.toLowerCase())));
  const clinicName = (id: string) => data?.clinics.find((c) => c.id === id)?.name ?? '';
  const ready = kind === 'internal'
    ? !!start && conflicts !== null && conflicts.length === 0
    : clinicIds.length > 0 && chosenInvitees.length > 0;
  const days = Array.from({ length: 14 }, (_, i) => dubaiDay(i));

  return (
    <div role="dialog" aria-modal="true" aria-label="New meeting" className="fixed inset-0 z-[300] flex items-start sm:items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={() => !send.isPending && onClose()}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl my-4 rounded-xl border border-slate-800 bg-bg-card shadow-2xl" data-testid="new-meeting-dialog">
        <div className="flex items-start gap-3 p-4 sm:p-5 border-b border-slate-800">
          <CalendarPlus size={18} className="text-accent-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-muted-strong">{sent ? 'Booking page sent' : 'New meeting'}</h3>
            <p className="text-xs text-muted mt-1">{sent
              ? 'The clinic picks from the times all of you are free. You and the team get the invite the moment they pick.'
              : 'Only times when everyone is free: working hours, off days, meetings and shoots are all checked.'}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-muted hover:text-muted-strong" aria-label="Close"><X size={16} /></button>
        </div>

        {sent ? (
          <div className="p-5 space-y-4">
            <p className="text-sm text-muted-strong">Sent to {sent.invitees.map((i) => i.name).join(', ')}.</p>
            {sent.link && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" icon={<Copy size={13} />} onClick={async () => { try { await navigator.clipboard.writeText(sent.link!); toast.success('Link copied'); } catch { toast.error('Copy did not work'); } }}>Copy link</Button>
                <Button size="sm" variant="secondary" onClick={() => { onClose(); nav(`/meetings/${sent.id}`); }}>Open the meeting</Button>
              </div>
            )}
            <div className="flex justify-end"><Button size="sm" onClick={onClose}>Done</Button></div>
          </div>
        ) : (
          <div className="p-4 sm:p-5 space-y-6">
            {/* kind */}
            <div className="inline-flex rounded-lg border border-slate-700 p-0.5" role="tablist" aria-label="Who is it with">
              {([['internal', 'Team only'], ['client', 'With a clinic']] as const).map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)} data-testid={`kind-${k}`}
                  className={cn('px-3 py-1.5 text-sm rounded-md min-h-[36px]', kind === k ? 'bg-accent-600 text-white' : 'text-muted hover:text-muted-strong')}>{l}</button>
              ))}
            </div>

            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Title (optional), e.g. October plan with Dr. Amal"
              className="w-full h-10 px-3 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong" data-testid="meeting-title" />

            <div className="grid gap-6 lg:grid-cols-2">
              {/* people */}
              <section>
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wide text-muted"><Users size={12} /> Our side</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <label className={`px-2 py-1 rounded-full text-xs inline-flex items-center gap-1.5 cursor-pointer ${iAttend ? 'bg-accent-600/20 text-accent-300' : 'bg-slate-800 text-muted line-through'}`}
                    title="Untick when you only book it for others: your own hours then do not limit the times" data-testid="meeting-i-attend">
                    <input type="checkbox" className="accent-accent-400" checked={iAttend} onChange={(e) => setIAttend(e.target.checked)} /> You
                  </label>
                  {attendees.map((id) => (
                    <button key={id} type="button" onClick={() => toggle(attendees, setAttendees, id)} className="px-2 py-1 rounded-full bg-slate-800 text-xs text-muted-strong inline-flex items-center gap-1">
                      {data?.team.find((p) => p.id === id)?.name ?? '...'} <X size={11} />
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-2.5 text-muted" />
                  <input value={peopleFilter} onChange={(e) => setPeopleFilter(e.target.value)} placeholder="Add people" data-testid="people-filter"
                    className="w-full h-9 pl-8 pr-3 rounded-lg bg-bg border border-slate-800 text-sm" />
                </div>
                <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-slate-800/60">
                  {team.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-800/40 cursor-pointer" data-testid="person-option">
                      <input type="checkbox" className="accent-accent-400" checked={attendees.includes(p.id)} onChange={() => toggle(attendees, setAttendees, p.id)} />
                      <span className="flex-1 text-muted-strong truncate">{p.name}</span>
                      <span className="text-[10px] text-muted truncate">{p.designations.slice(0, 2).map((d) => d.replace(/_/g, ' ')).join(', ')}</span>
                    </label>
                  ))}
                </div>
              </section>

              {/* clinics + their people */}
              <section>
                <div className="text-[11px] uppercase tracking-wide text-muted mb-2">{kind === 'client' ? 'Clinics and who to invite' : 'Clinics (for the agenda, optional)'}</div>
                <div className="flex flex-wrap gap-1.5">
                  {(data?.clinics ?? []).map((c) => (
                    <button key={c.id} type="button" onClick={() => toggle(clinicIds, setClinicIds, c.id)} aria-pressed={clinicIds.includes(c.id)} data-testid="clinic-option"
                      className={cn('px-2.5 py-1 rounded-full border text-xs', clinicIds.includes(c.id) ? 'border-accent-400 text-accent-300 bg-accent-600/15' : 'border-slate-700 text-muted')}>{c.name}</button>
                  ))}
                </div>
                {kind === 'client' && clinicIds.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {(data?.invitees ?? []).map((i) => (
                      <label key={i.email} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-slate-800/40 cursor-pointer" data-testid="invitee-option">
                        <input type="checkbox" className="accent-accent-400" checked={invitees.includes(i.email)} onChange={() => toggle(invitees, setInvitees, i.email)} />
                        <span className="flex-1 min-w-0"><span className="text-muted-strong">{i.name}</span>
                          <span className="text-[11px] text-muted"> · {i.kind === 'doctor' ? 'doctor' : 'clinic'} · {clinicName(i.client_id)}</span></span>
                      </label>
                    ))}
                    {extra.map((e) => (
                      <div key={e.email} className="flex items-center gap-2 px-2 py-1 text-sm"><Check size={13} className="text-emerald-400" /><span className="flex-1">{e.name} <span className="text-muted text-[11px]">{e.email}</span></span>
                        <button type="button" onClick={() => setExtra(extra.filter((x) => x.email !== e.email))} aria-label="Remove"><X size={13} /></button></div>
                    ))}
                    <div className="flex gap-1.5 pt-1">
                      <input value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} placeholder="Name" className="flex-1 min-w-0 h-8 px-2 rounded bg-bg border border-slate-800 text-xs" />
                      <input value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} placeholder="Email" type="email" className="flex-1 min-w-0 h-8 px-2 rounded bg-bg border border-slate-800 text-xs" />
                      <Button size="sm" variant="secondary" icon={<Plus size={12} />} disabled={!/^\S+@\S+\.\S+$/.test(adding.email.trim())}
                        onClick={() => { setExtra([...extra, { email: adding.email.trim().toLowerCase(), name: adding.name.trim() || adding.email.trim() }]); setAdding({ name: '', email: '' }); }}>Add</Button>
                    </div>
                  </div>
                )}
              </section>
            </div>

            {/* agenda */}
            <section>
              <div className="text-[11px] uppercase tracking-wide text-muted mb-2">Agenda</div>
              {items.length > 0 && (
                <ol className="mb-2 space-y-1" data-testid="agenda-list">
                  {items.map((it, i) => (
                    <li key={`${it.kind}-${it.id ?? it.label}`} className="flex items-center gap-2 text-sm rounded-lg bg-slate-800/40 px-2 py-1.5">
                      <span className="text-[10px] uppercase text-muted w-16 shrink-0">{it.kind}</span>
                      <span className="flex-1 min-w-0 truncate text-muted-strong">{it.label}</span>
                      <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label="Remove from the agenda"><X size={13} /></button>
                    </li>
                  ))}
                </ol>
              )}
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-2.5 text-muted" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={clinicIds.length ? 'Find a creative or strategy' : 'Pick a clinic to add its work'}
                      disabled={!clinicIds.length} className="w-full h-9 pl-8 pr-3 rounded-lg bg-bg border border-slate-800 text-sm disabled:opacity-50" data-testid="agenda-search" />
                  </div>
                  {clinicIds.length > 0 && (
                    <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-800/60">
                      {(data?.agenda ?? []).filter((a) => !items.some((i) => i.id === a.id)).slice(0, 25).map((a) => (
                        <button key={a.id} type="button" onClick={() => setItems([...items, { kind: a.kind, id: a.id, label: a.label }])} data-testid="agenda-option"
                          className="w-full text-left flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-800/40">
                          <Plus size={12} className="text-muted" /><span className="flex-1 truncate">{a.label}</span>
                          {a.script_pending && <span className="text-[10px] text-amber-300">script waiting</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1.5 items-start">
                  <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Or a topic, e.g. Ramadan campaign" maxLength={300}
                    onKeyDown={(e) => { if (e.key === 'Enter' && topic.trim()) { setItems([...items, { kind: 'topic', label: topic.trim() }]); setTopic(''); } }}
                    className="flex-1 min-w-0 h-9 px-3 rounded-lg bg-bg border border-slate-800 text-sm" data-testid="topic-input" />
                  <Button size="sm" variant="secondary" disabled={!topic.trim()} onClick={() => { setItems([...items, { kind: 'topic', label: topic.trim() }]); setTopic(''); }}>Add</Button>
                </div>
              </div>
            </section>

            {/* format */}
            <section className="grid gap-3 sm:grid-cols-3">
              <label className="text-xs text-muted">Length
                <select value={minutes} onChange={(e) => { setMinutes(Number(e.target.value)); setStart(null); }} className="mt-1 w-full h-9 px-2 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong">
                  {LENGTHS.map((l) => <option key={l} value={l}>{l} minutes</option>)}
                </select>
              </label>
              <div className="text-xs text-muted">Where
                <div className="mt-1 inline-flex rounded-lg border border-slate-700 p-0.5 w-full">
                  <button type="button" onClick={() => setWithMeet(true)} aria-pressed={withMeet} className={cn('flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md text-xs', withMeet ? 'bg-slate-700 text-white' : 'text-muted')}><Video size={12} /> Google Meet</button>
                  <button type="button" onClick={() => setWithMeet(false)} aria-pressed={!withMeet} className={cn('flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md text-xs', !withMeet ? 'bg-slate-700 text-white' : 'text-muted')}><MapPin size={12} /> In person</button>
                </div>
              </div>
              {!withMeet && (
                <label className="text-xs text-muted">Place
                  <input value={location} onChange={(e) => setLocation(e.target.value)} maxLength={200} placeholder="e.g. KAF studio" className="mt-1 w-full h-9 px-2 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong" />
                </label>
              )}
            </section>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} placeholder="Note for everyone (optional)"
              className="w-full px-3 py-2 rounded-lg bg-bg border border-slate-800 text-sm" />

            {/* when */}
            {kind === 'internal' ? (
              <section data-testid="when-internal">
                <div className="text-[11px] uppercase tracking-wide text-muted mb-2">When (Dubai time)</div>
                {best.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    <span className="text-xs text-muted">Best times:</span>
                    {best.map((s) => (
                      <button key={s} type="button" data-testid="best-time" onClick={() => { setDay(new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(new Date(s))); setStart(s); }}
                        className={cn('px-2.5 py-1 rounded-full border text-xs', start === s ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200' : 'border-emerald-600/50 text-emerald-300')}>{whenLabel(s)}</button>
                    ))}
                  </div>
                )}
                {free.data && free.data.slots.length === 0 && <p className="text-xs text-amber-300 mb-2">No time in the next 10 days suits everyone. Pick fewer people, a shorter length, or look at the grid.</p>}
                <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2">
                  {days.map((d) => (
                    <button key={d} type="button" onClick={() => { setDay(d); setStart(null); }} aria-pressed={day === d}
                      className={cn('shrink-0 px-2.5 py-1.5 rounded-lg border text-xs', day === d ? 'border-accent-400 bg-accent-600/20 text-accent-200' : 'border-slate-700 text-muted')}>{dayChip(d)}</button>
                  ))}
                </div>
                <AvailabilityGrid userIds={people} day={day} minutes={minutes} selected={start} onPick={(iso) => setStart(iso)} />
                <div className="mt-3 text-sm" aria-live="polite" data-testid="check-result">
                  {start && conflicts === null && <span className="text-muted">Checking {whenLabel(start)}...</span>}
                  {start && conflicts && conflicts.length === 0 && <span className="text-emerald-300">Everyone is free: {whenLabel(start)}, {minutes} minutes</span>}
                  {start && conflicts && conflicts.length > 0 && (
                    <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3">
                      <div className="text-rose-200 font-medium mb-1">Not available at {whenLabel(start)}</div>
                      <ul className="text-xs text-rose-100 space-y-0.5">{conflicts.map((c) => <li key={c.user_id}>{c.name} {REASON[c.reason]}{c.reason !== 'hours' && c.label && !['Off', 'Away'].includes(c.label) ? ` (${c.label})` : ''}</li>)}</ul>
                      <div className="text-[11px] text-muted mt-1">Pick a green column in the Everyone row, or one of the best times.</div>
                    </div>
                  )}
                </div>
              </section>
            ) : (
              <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm" data-testid="when-client">
                {free.data ? (
                  <span className="text-muted-strong">The clinic will see <b>{free.data.slots.length}</b> times in the next 10 days when all {people.length} of you are free.
                    {free.data.away.length > 0 && <span className="text-muted"> ({free.data.away.join(', ')} {free.data.away.length === 1 ? 'has' : 'have'} something booked in that window.)</span>}</span>
                ) : <span className="text-muted">Working out the free times...</span>}
              </section>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button disabled={!ready} loading={send.isPending} onClick={() => send.mutate()} data-testid="send-meeting">
                {kind === 'internal' ? 'Send invites' : 'Send booking page'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

